import React, {FormEvent, useCallback, useContext, useEffect, useMemo, useState,} from "react";
import {useTranslation} from "react-i18next";
import {ActionButton, MaskedTextField, PrimaryButton, ProgressIndicator, Stack, Text,} from "@fluentui/react";
//@ts-ignore radi...
import Flag from "react-world-flags";
import Axios from "axios";
import {useBoolean} from "@fluentui/react-hooks";
import {TitleAndContentLayout} from "./TitleAndContentLayout";
import {LanguageContext} from "../contexts/LanguageContext";
import {CustomSearchBox} from "./CustomSearchBox";
import {AuthContext} from "../contexts/AuthContext";
import {CultureDto, OtherSettingsDto, TimeZoneDto} from "../API";
import {GetApiError} from "./ShowApiError";
import {UserInfoHelper} from "../helpers/UserInfoHelper";
import {OnixRoleEnum} from "../enums/OnixRoleEnum";

export const UserSettingsOther = () => {
    const {t} = useTranslation();
    const {switchLanguage, moment} = useContext(LanguageContext);
    const [otherSettings, setOtherSettings] = useState<OtherSettingsDto>();
    const [timeFormat, setTimeFormat] = useState<string>();
    const {UserInfo, FetchUserInfo} = useContext(AuthContext);
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] =
        useBoolean(false);
    const [error, setError] = useState();
    const [tzTesting, setTzTesting] = useState<any>();

    const isAdmin = useMemo(
        () => UserInfoHelper.HasRole(OnixRoleEnum.ADMIN, UserInfo),
        [UserInfo],
    );

    const reloadTimezones = useCallback(async () => {
        if (isAdmin) {
            Axios.get("TimeZones/TestingTZ").then((res) => setTzTesting(res.data));
        }
    }, [isAdmin]);

    useEffect(() => {
        reloadTimezones();
    }, [reloadTimezones]);

    const reloadData = useCallback(() => {
        setLoading();
        Axios.get("OtherSettings")
            .then(({data}) => setOtherSettings(data))
            .finally(setNotLoading);
    }, []);

    useEffect(() => {
        reloadData();
    }, []);

    const setCulture = useCallback((culture: string) => {
        setLoading();
        Axios.put("OtherSettings/Culture", {}, {params: {culture}})
            .then(({data}) => setOtherSettings(data))
            .then(() => setError(undefined))
            .catch(({response: {data}}) => setError(data))
            .finally(setNotLoading);
    }, []);

    const setTimeZone = useCallback((timeZone: string) => {
        setLoading();
        Axios.put("OtherSettings/TimeZone", {}, {params: {timeZone}})
            .then(({data}) => setOtherSettings(data))
            .then(() => setError(undefined))
            .catch(({response: {data}}) => setError(data))
            .finally(setNotLoading);
    }, []);

    const setAutomaticLogout = useCallback((timeSpan: string) => {
        setLoading();
        Axios.put("OtherSettings/AutomaticLogout", {}, {params: {timeSpan}})
            .then(({data}) => setOtherSettings(data))
            .then(() => setError(undefined))
            .then(() => FetchUserInfo())
            .catch(({response: {data}}) => setError(data))
            .finally(setNotLoading);
    }, []);

    useEffect(() => {
        setLoading();
        Axios.get("TimeZones/Localized")
            .then(({data}) => setTimeFormat(data))
            .finally(setNotLoading);
    }, [otherSettings?.timeZone, otherSettings?.culture]);

    const submitAutomaticLogout = useCallback(
        (event: FormEvent) => {
            if (event) event.preventDefault();

            setAutomaticLogout(otherSettings?.automaticLogout as any);
        },
        [otherSettings],
    );

    if (!otherSettings) return null;

    return (
        <TitleAndContentLayout
            title={t("other-settings")}
            lessPadding
            isSubtitle
            pad
        >
            <Stack tokens={{childrenGap: 10}}>
                <Stack horizontal tokens={{childrenGap: 10}}>
                    <Stack tokens={{childrenGap: 10}} horizontalAlign="stretch" grow>
                        <Text variant="large">{t("localization")}</Text>
                        <Stack tokens={{childrenGap: 10}}>
                            <Text style={{fontWeight: "bold"}}>{t("language")}</Text>
                            <Stack horizontal tokens={{childrenGap: 10}}>
                                <ActionButton
                                    disabled={loading}
                                    text={t("croatian")}
                                    onRenderIcon={() => <Flag code={"HR"} width={20}/>}
                                    onClick={() =>
                                        switchLanguage(
                                            "HR",
                                            UserInfo?.user?.companySettings?.connectionName,
                                        )
                                    }
                                />
                                <ActionButton
                                    disabled={loading}
                                    text={t("english")}
                                    onRenderIcon={() => <Flag code={"GB"} width={20}/>}
                                    onClick={() =>
                                        switchLanguage(
                                            "EN",
                                            UserInfo?.user?.companySettings?.connectionName,
                                        )
                                    }
                                />
                                <ActionButton
                                    disabled={loading}
                                    text={t("italian")}
                                    onRenderIcon={() => <Flag code={"IT"} width={20}/>}
                                    onClick={() =>
                                        switchLanguage(
                                            "IT",
                                            UserInfo?.user?.companySettings?.connectionName,
                                        )
                                    }
                                />
                            </Stack>
                        </Stack>
                        <Stack tokens={{childrenGap: 10}}>
                            <Text style={{fontWeight: "bold"}}>{t("culture")}</Text>
                            <CustomSearchBox<CultureDto>
                                placeholder={t("culture")}
                                searchUrl={"/Cultures"}
                                type={"TENANT_OLD"}
                                listItemKey={"name"}
                                selected={otherSettings?.culture}
                                titleKey={"name"}
                                disabled={loading}
                                subtitleKey={"nativeName"}
                                errorMessage={GetApiError(error, "culture")}
                                optionSelected={(v) => (v ? setCulture(v.name) : () => {
                                })}
                            />
                        </Stack>
                        <Stack tokens={{childrenGap: 10}}>
                            <Text style={{fontWeight: "bold"}}>{t("timezone")}</Text>
                            <CustomSearchBox<TimeZoneDto>
                                placeholder={t("timezone")}
                                searchUrl={"/TimeZones"}
                                type={"TENANT_OLD"}
                                listItemKey={"id"}
                                selected={otherSettings?.timeZone}
                                disabled={loading}
                                titleKey={"id"}
                                subtitleKey={"displayName"}
                                errorMessage={GetApiError(error, "timezone")}
                                optionSelected={(v) => (v ? setTimeZone(v.id) : () => {
                                })}
                            />
                        </Stack>
                        <Stack tokens={{childrenGap: 10}}>
                            <Text style={{fontWeight: "bold"}}>{t("time-format")}</Text>
                            <Text>{timeFormat}</Text>
                        </Stack>
                    </Stack>
                    <Stack tokens={{childrenGap: 10}} horizontalAlign="stretch" grow>
                        <Text variant="large">{t("system")}</Text>
                        <form onSubmit={submitAutomaticLogout}>
                            <Stack tokens={{childrenGap: 10}}>
                                <Text style={{fontWeight: "bold"}}>
                                    {t("automatic-logout")}
                                </Text>
                                <MaskedTextField
                                    label={"(HH:MM:SS / HH.MM.SS)"}
                                    mask={"99:99:99"}
                                    value={otherSettings.automaticLogout as any}
                                    errorMessage={GetApiError(error, "timeSpan")}
                                    onChange={(_, v) =>
                                        setOtherSettings({
                                            ...otherSettings,
                                            automaticLogout: v as any,
                                        })
                                    }
                                />
                                <PrimaryButton
                                    type="submit"
                                    text={t("save")}
                                    disabled={loading}
                                />
                            </Stack>
                        </form>
                    </Stack>
                </Stack>
            </Stack>
            {isAdmin && (
                <Stack>
                    <br/>
                    <Text variant="large">{t("administration")}</Text>
                    <pre>{JSON.stringify(tzTesting, null, 2)}</pre>
                    <PrimaryButton text={t("test-timezones")} onClick={reloadTimezones}/>
                </Stack>
            )}
            {loading && <ProgressIndicator/>}
        </TitleAndContentLayout>
    );
};
